import { FC, useCallback, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';

import { OrderStatus } from '@app/api/models/order';
import { RequestPayload } from '@app/api/types';
import { OrderStatusListPayload } from '@app/api/orders';
import { Dialog, SwitchField, TextField } from '@app/components/common';
import { QueryTable } from '@app/components/common/table';
import { useQueryTable } from '@app/components/common/table/query-table';
import { useAlert, useApi, useFetchData } from '@app/hooks';

import { useColumns } from './columns';

type StatusDraft = Partial<OrderStatus>;

const StatusDialog: FC<{
  open: boolean;
  initial?: StatusDraft;
  onClose: () => void;
}> = ({ open, initial, onClose }) => {
  const api = useApi();
  const { error, success } = useAlert();
  const { refetch } = useQueryTable();
  const [draft, setDraft] = useState<StatusDraft>(initial || { is_active: true });
  const [loading, setLoading] = useState(false);

  const onSave = async () => {
    if (!draft.name) {
      error('Введите название');
      return;
    }
    setLoading(true);
    try {
      if (draft.id) {
        const response = await api.orders.updateStatus(draft.id, {
          name: draft.name,
          is_active: !!draft.is_active,
        });
        if (response?.code === 200) {
          success('Статус обновлён');
          refetch();
          onClose();
        }
      } else {
        const response = await api.orders.createStatus({ name: draft.name });
        if (response?.code === 200) {
          success('Статус создан');
          refetch();
          onClose();
        }
      }
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={draft.id ? 'Редактирование статуса' : 'Создание статуса'}
      footerless
    >
      <Flex gap={3} flexDirection="column">
        <TextField
          id="name"
          label="Название"
          required
          value={draft.name || ''}
          onChange={(value) => setDraft({ ...draft, name: value })}
        />
        {!!draft.id && (
          <SwitchField
            id="is_active"
            label="Активен"
            value={draft.is_active}
            onChange={(value) => setDraft({ ...draft, is_active: value })}
          />
        )}
        <Flex justifyContent="flex-end" gap={2}>
          <Button variant="outline" colorScheme="gray" onClick={onClose} isDisabled={loading}>
            Отмена
          </Button>
          <Button variant="solid" colorScheme="brand" onClick={onSave} isLoading={loading}>
            Сохранить
          </Button>
        </Flex>
      </Flex>
    </Dialog>
  );
};

export const OrderStatusesTable: FC = () => {
  const api = useApi();
  const [selected, setSelected] = useState<StatusDraft>();
  const [open, setOpen] = useState(false);

  const onEdit = useCallback((status: OrderStatus) => {
    setSelected(status);
    setOpen(true);
  }, []);

  const columns = useColumns({ onEdit });

  const fetchStatuses = useCallback(
    (payload: RequestPayload) =>
      api.orders.getStatuses({
        limit: payload.limit,
        offset: payload.offset,
        sort: payload.sort,
        filter: payload.filter as OrderStatusListPayload['filter'],
      }),
    [api]
  );

  const [fetchData] = useFetchData<OrderStatus>({ method: fetchStatuses });

  return (
    <QueryTable<OrderStatus>
      title="Статусы заказов"
      columns={columns}
      fetchData={fetchData}
      onCreateClick={() => {
        setSelected({ is_active: true });
        setOpen(true);
      }}
    >
      {open && (
        <StatusDialog
          key={selected?.id || 'create'}
          open={open}
          initial={selected}
          onClose={() => setOpen(false)}
        />
      )}
    </QueryTable>
  );
};

export default OrderStatusesTable;
